import moment from 'moment';
import { closeLoader } from '../../data/screenLoader/actions';
import { RequestActionType } from '../constants/enumTypes';
import api from '../utils/api';
import Common from './Common';
import Session, { IS_BUYSIDE } from './Session';

// LNCT has no custom rate adjustment type, so a CustomRateAdjustment rides along in the
// CUSTOM_PRICE_ADJUSTMENT payload with the |CRA| description marker that
// setBuySideAdjustmentsAction and isCustomRateAdjustment use to recover the type on read.
export const eppsToLNCTAdjustmentType = {
  CustomPriceAdjustment: 'CUSTOM_PRICE_ADJUSTMENT',
  CustomRateAdjustment: 'CUSTOM_PRICE_ADJUSTMENT',
  ReLockFeeAdjustment: 'RE_LOCK_FEE_ADJUSTMENT',
  LockExtensionAdjustment: 'LOCK_EXTENSION_ADJUSTMENT',
  CorporatePriceConcession: 'CORPORATE_PRICE_CONCESSION',
  BranchPriceConcession: 'BRANCH_PRICE_CONCESSION',
};

export const CUSTOM_RATE_ADJUSTMENT_MARKER = '|CRA|';

const markCustomRateDescription = (description) => {
  const text = description ?? '';
  return text.includes(CUSTOM_RATE_ADJUSTMENT_MARKER)
    ? text
    : `${CUSTOM_RATE_ADJUSTMENT_MARKER}-${text}`.trim();
};

export const convertEPPSAdjustmentsToLNCT = (buysideAdjustments) => {
  // Array<Record<keyof AdjustmentType, {uuid: string, price: number, description: string}[]>>
  const response = (buysideAdjustments ?? []).reduce((sum, curr) => {
    const isCustomRate = curr.adjustmentType === 'CustomRateAdjustment';
    const newAdjustment = {
      uuid: curr.uuid,
      points: curr.price && !Number.isNaN(curr.price) ? curr.price : null,
      rate: curr.rate && !Number.isNaN(curr.rate) ? curr.rate : null,
      description: isCustomRate
        ? markCustomRateDescription(curr.description)
        : curr.description,
      approvedBy: curr.approvedBy,
      approvedReason: curr?.tempApprovedReason || curr.approvedReason || '',
      paidByGuid: curr.paidByGuid ?? '',
      adjustmentReasonGuid: curr.adjustmentReasonGuid ?? '',
      approvalDate: curr.approvalDate
        ? moment(curr.approvalDate).format('YYYY-MM-DD')
        : null,
    };
    const newAdjustmentType = Object.values(eppsToLNCTAdjustmentType).includes(
      curr.adjustmentType,
    )
      ? curr.adjustmentType
      : eppsToLNCTAdjustmentType[curr.adjustmentType];
    if (!newAdjustmentType) {
      return sum;
    }
    return {
      ...sum,
      [newAdjustmentType]: sum[newAdjustmentType]
        ? [...sum[newAdjustmentType], newAdjustment]
        : [newAdjustment],
    };
  }, {});

  return response;
};

export const createAdjustments = async (
  loanData,
  adjustments,
  parPricingType,
) => {
  const loanNumber = loanData?.loanInformation?.loanId;

  await Promise.all(
    Object.entries(adjustments).map(([key, adjustmentsInGroup]) =>
      api.createAdjustments({
        loanNumber,
        adjustments: adjustmentsInGroup,
        adjustmentType: key,
        parPricingType,
      }),
    ),
  );

  return null;
};

// Formats the effective date sent when fetching rates, so lockRates can send the same value
export const formatRatesEffectiveDate = (date, isCustom = false) =>
  isCustom
    ? moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
    : (moment as any)
        .tz(date, 'America/New_York')
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

const populateOriginDate = (loanData, obj, originalEffectiveDate?) => {
  const isBuySide = Session.get(IS_BUYSIDE);
  const updatedRelockLoanData = {
    effectiveDate: formatRatesEffectiveDate(obj.requestedDate),
    specificProgramID: loanData.relockRequest?.specificProgramID,
    specificRateDataID: loanData.relockRequest?.specificRateDataID,
  };
  let wcpRequest = loanData.wcpRequest;
  if (originalEffectiveDate && obj.requestedDate) {
    const initialDate = new Date(originalEffectiveDate);
    const updatedDate = new Date(obj.requestedDate);
    if (initialDate.getTime() !== updatedDate.getTime()) {
      wcpRequest = null;
    }
  }
  return {
    ...loanData,
    wcpRequest,
    requestAction: isBuySide
      ? RequestActionType.BuyRequest.toString()
      : RequestActionType.SellRequest.toString(),
    relockRequest: updatedRelockLoanData,
  };
};

const getWebhookRelockData = async (loanData, dispatch, action) => {
  let apiResult = null;
  await Common.getWebhookData(
    loanData,
    await Common.getOriginId(),
    dispatch,
    action,
    await Common.getPATToken(),
  )
    .then((result) => {
      if (result && result.code !== undefined) {
        apiResult = null;
      } else {
        apiResult = result;
      }
    })
    .catch(() => {
      dispatch(closeLoader());
    });
  return apiResult;
};

const getRelockData = async (
  loanData,
  dispatch,
  action,
  lockRequestsEvent,
  originalEffectiveDate?,
) => {
  let fetchRelockData;
  const requests = lockRequestsEvent.map((item) => {
    return getWebhookRelockData(
      populateOriginDate(loanData, item, originalEffectiveDate),
      dispatch,
      action,
    );
  });
  await Promise.all(requests).then((results) => {
    fetchRelockData = results;
  });
  return fetchRelockData;
};

export const getRatesData = async (
  loanData,
  date,
  isCustom,
  dispatch,
  action,
  originalEffectiveDate,
) => {
  if (originalEffectiveDate && date) {
    const initialDate = new Date(originalEffectiveDate);
    const updatedDate = new Date(date);
    if (initialDate.getTime() !== updatedDate.getTime()) {
      loanData.wcpRequest = null;
    }
  }
  const effectiveDate = formatRatesEffectiveDate(date, isCustom);
  const updatedLoanData = {
    ...loanData,
    requestAction: Session.get(IS_BUYSIDE)
      ? RequestActionType.BuyRequest.toString()
      : RequestActionType.SellRequest.toString(),
    relockRequest: {
      ...loanData.relockRequest,
      effectiveDate,
    },
  };
  const ratesData = await getWebhookRelockData(
    updatedLoanData,
    dispatch,
    action,
  );
  return ratesData;
};

export default getRelockData;
